import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Clock, 
  Users, 
  ChevronLeft, 
  Heart,
  Lightbulb,
  Sparkles,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract the 11-char YouTube video id from common URL formats (incl. unlisted).
const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/))([\w-]{11})/
  );
  return m ? m[1] : null;
};

// Click-to-play YouTube embed: shows thumbnail first, loads iframe on click.
const VideoEmbed = ({ url, label }) => {
  const [playing, setPlaying] = useState(false);
  const id = getYouTubeId(url);
  if (!id) return null;
  return (
    <div className="mb-8" data-testid="recipe-video">
      <div className="relative rounded-2xl overflow-hidden shadow-soft aspect-video bg-black">
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
            title={label}
            allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            data-testid="recipe-video-iframe"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full h-full"
            data-testid="recipe-video-play"
            aria-label={label}
          >
            <img
              src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
              alt={label}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 md:w-9 md:h-9 text-white fill-current ml-1" />
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default function RecipeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedField, user, language } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());

  // Scroll to top whenever recipe id changes (fixes PC view opening at bottom)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const response = await fetch(`${API_URL}/api/recipes/${id}`);
        if (!response.ok) throw new Error('Recipe not found');
        const data = await response.json();
        setRecipe(data);

        // Check if favorite
        if (user) {
          const favResponse = await fetch(`${API_URL}/api/favorites/check/${id}`, {
            credentials: 'include'
          });
          if (favResponse.ok) {
            const favData = await favResponse.json();
            setIsFavorite(favData.is_favorite);
          }
        }
      } catch (error) {
        console.error('Failed to fetch recipe:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id, user]);

  const toggleFavorite = async () => {
    if (!user) return;

    try {
      if (isFavorite) {
        await fetch(`${API_URL}/api/favorites/${id}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } else {
        await fetch(`${API_URL}/api/favorites/${id}`, {
          method: 'POST',
          credentials: 'include'
        });
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground text-lg mb-4">{t('error')}</p>
          <Button onClick={() => navigate('/recipes')} variant="outline">
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t('recipes')}
          </Button>
        </div>
      </div>
    );
  }

  const name = getLocalizedField(recipe, 'name');
  const description = getLocalizedField(recipe, 'description');
  const instructions = getLocalizedField(recipe, 'instructions') || recipe.instructions || [];
  const tips = getLocalizedField(recipe, 'tips');

  const getDifficultyLabel = (diff) => {
    const labels = {
      sk: { easy: 'Jednoduché', medium: 'Stredné', hard: 'Náročné' },
      cz: { easy: 'Jednoduché', medium: 'Střední', hard: 'Náročné' }
    };
    return labels[language]?.[diff] || diff;
  };

  return (
    <div className="min-h-screen bg-cream-subtle" data-testid="recipe-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src={recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=1200'}
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Back button */}
        <Button
          variant="ghost"
          className="absolute top-4 left-4 bg-white/90 hover:bg-white rounded-full"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>

        {/* Favorite button */}
        <Button
          variant="ghost"
          className={cn(
            "absolute top-4 right-4 rounded-full",
            isFavorite ? "bg-primary text-white hover:bg-primary-hover" : "bg-white/90 hover:bg-white"
          )}
          onClick={toggleFavorite}
          data-testid="favorite-button"
        >
          <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
        </Button>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm rounded-full mb-3">
              {getDifficultyLabel(recipe.difficulty)}
            </span>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white">
              {name}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Meta info */}
        <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-border">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm">{t('prepTime')}</p>
              <p className="font-medium text-foreground">{recipe.prep_time} {t('minutes')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-5 h-5 text-secondary" />
            <div>
              <p className="text-sm">{t('cookTime')}</p>
              <p className="font-medium text-foreground">{recipe.cook_time} {t('minutes')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="w-5 h-5 text-accent-yellow" />
            <div>
              <p className="text-sm">{t('servings')}</p>
              <p className="font-medium text-foreground">{recipe.servings} {t('portions')}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        {description && (
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            {description}
          </p>
        )}

        {/* Video recept */}
        {recipe.video_url && getYouTubeId(recipe.video_url) && (
          <VideoEmbed url={recipe.video_url} label={name} />
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {/* Ingredients */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-soft sticky top-24">
              <h2 className="font-serif text-xl font-semibold text-text-heading mb-4">
                {t('ingredients')}
              </h2>
              <ul className="space-y-3">
                {recipe.ingredients?.map((ing, index) => (
                  <li 
                    key={index}
                    className="flex items-start gap-3 cursor-pointer group"
                    onClick={() => toggleIngredient(index)}
                  >
                    <Checkbox
                      checked={checkedIngredients.has(index)}
                      className="mt-0.5"
                    />
                    <span className={cn(
                      "text-muted-foreground transition-all",
                      checkedIngredients.has(index) && "line-through opacity-50"
                    )}>
                      <span className="font-medium text-foreground">{ing.amount} {ing.unit}</span>{' '}
                      {ing.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Instructions */}
          <div className="md:col-span-2">
            <h2 className="font-serif text-xl font-semibold text-text-heading mb-6">
              {t('instructions')}
            </h2>
            <ol className="space-y-6">
              {instructions.map((step, index) => (
                <li key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <p className="text-muted-foreground leading-relaxed pt-1">
                    {step}
                  </p>
                </li>
              ))}
            </ol>

            {/* Tips from Lucia */}
            {tips && (
              <div className="mt-10 p-6 bg-accent-cream rounded-2xl border border-accent-yellow/30">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent-yellow/30 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-accent text-xl text-primary mb-2">
                      {t('tipFromLucia')}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {tips}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Magic Five Reason */}
            {(recipe.magic_five_reason || recipe.magic_five_reason_cz) && (
              <div className="mt-6 p-6 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-accent text-xl text-primary mb-2">
                      {language === 'sk' ? 'Prečo Magická päťka?' : 'Proč Magická pětka?'}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {getLocalizedField(recipe, 'magic_five_reason')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Categories */}
        {recipe.category && recipe.category.length > 0 && (
          <div className="mt-10 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {recipe.meal_type?.map(type => (
                <Link
                  key={type}
                  to={`/recipes?meal_type=${encodeURIComponent(type)}`}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm hover:bg-primary/20 transition-colors"
                  data-testid={`chip-meal-${type}`}
                >
                  {type}
                </Link>
              ))}
              {recipe.season?.map(s => (
                <Link
                  key={s}
                  to={`/recipes?season=${encodeURIComponent(s)}`}
                  className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm hover:bg-secondary/20 transition-colors"
                  data-testid={`chip-season-${s}`}
                >
                  {s}
                </Link>
              ))}
              {recipe.category?.map(cat => (
                <Link
                  key={cat}
                  to={`/recipes?category=${encodeURIComponent(cat)}`}
                  className="px-3 py-1 bg-accent-yellow/20 text-amber-700 rounded-full text-sm hover:bg-accent-yellow/40 transition-colors"
                  data-testid={`chip-category-${cat}`}
                >
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
